import { z } from 'zod';
import type { ArtistInfo, FeedItem, NewsItem, PriceComparisonRow, SearchItem } from '../types';

const rawItemSchema = z.record(z.unknown());

const searchResponseSchema = z.object({
  resultCount: z.number().optional(),
  results: z.array(rawItemSchema).default([])
});

const feedResultSchema = z.object({
  id: z.string(),
  name: z.string(),
  artistName: z.string().optional(),
  url: z.string().optional(),
  artworkUrl100: z.string().optional(),
  artworkUrl60: z.string().optional(),
  releaseDate: z.string().optional(),
  kind: z.string().optional(),
  genres: z.array(z.object({ name: z.string().optional() })).optional()
}).passthrough();

const rssResponseSchema = z.object({
  feed: z.object({
    title: z.string().optional(),
    country: z.string().optional(),
    updated: z.string().optional(),
    results: z.array(feedResultSchema).default([])
  })
});

const legacyRssResponseSchema = z.object({
  feed: z.object({
    title: rawItemSchema.optional(),
    updated: rawItemSchema.optional(),
    entry: z.union([rawItemSchema, z.array(rawItemSchema)]).optional()
  }).passthrough()
});

const newsResponseSchema = z.object({
  source: z.string(),
  title: z.string().optional(),
  updated: z.string().optional(),
  items: z.array(z.object({
    id: z.string().optional(),
    title: z.string(),
    link: z.string().optional(),
    publishedAt: z.string().optional(),
    summary: z.string().optional(),
    imageUrl: z.string().optional(),
    categories: z.array(z.string()).default([])
  })).default([])
});

const compareResponseSchema = z.object({
  id: z.string(),
  rows: z.array(z.object({
    country: z.string(),
    status: z.enum(['available', 'unavailable', 'error']),
    name: z.string().optional(),
    price: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    url: z.string().optional(),
    message: z.string().optional()
  }))
});

const artistInfoSchema = z.object({
  name: z.string(),
  wikipedia: z.object({
    title: z.string(),
    summary: z.string(),
    imageUrl: z.string().optional(),
    pageUrl: z.string().optional()
  }).optional(),
  musicBrainz: z.object({
    id: z.string(),
    type: z.string().optional(),
    country: z.string().optional(),
    disambiguation: z.string().optional(),
    lifeSpan: z.string().optional(),
    tags: z.array(z.string()).default([]),
    url: z.string()
  }).optional()
});

export type SearchParams = {
  term: string;
  country: string;
  media: string;
  entity?: string;
  attribute?: string;
  limit: number;
  lang: 'en_us' | 'ja_jp';
  explicit: 'Yes' | 'No';
};

export type RssParams = {
  storefront: string;
  media: string;
  feed: string;
  limit: number;
  type: string;
};

export type LegacyRssParams = {
  storefront: string;
  feed: 'topmovies' | 'toptvepisodes' | 'toptvseasons';
  limit: number;
};

export async function searchItunes(params: SearchParams) {
  const data = await getJson('/api/search', params);
  const parsed = searchResponseSchema.parse(data);
  return parsed.results.map(normalizeSearchItem).filter(Boolean) as SearchItem[];
}

export async function lookupItunes(params: Record<string, string | number | undefined>) {
  const data = await getJson('/api/lookup', params);
  const parsed = searchResponseSchema.parse(data);
  return parsed.results.map(normalizeSearchItem).filter(Boolean) as SearchItem[];
}

export async function getRssFeed(params: RssParams) {
  const data = await getJson('/api/rss', params);
  const parsed = rssResponseSchema.parse(data);
  return {
    title: parsed.feed.title ?? '',
    country: parsed.feed.country,
    updated: parsed.feed.updated,
    items: parsed.feed.results.map((item): FeedItem => ({
      id: item.id,
      name: item.name,
      artistName: item.artistName,
      url: item.url,
      artworkUrl: upgradeArtwork(item.artworkUrl100 ?? item.artworkUrl60),
      releaseDate: item.releaseDate,
      kind: item.kind,
      genres: item.genres?.map((genre) => genre.name).filter(Boolean) as string[] ?? [],
      raw: item
    }))
  };
}

export async function getLegacyRssFeed(params: LegacyRssParams) {
  const data = await getJson('/api/legacy-rss', params);
  const parsed = legacyRssResponseSchema.parse(data);
  const entries = toArray(parsed.feed.entry);
  return {
    title: readLegacyLabel(parsed.feed.title),
    updated: readLegacyLabel(parsed.feed.updated),
    items: entries.map((item) => normalizeLegacyRssItem(item, params.feed)).filter(Boolean) as SearchItem[]
  };
}

export async function getNews(source: string) {
  const data = await getJson('/api/news', { source });
  const parsed = newsResponseSchema.parse(data);
  return {
    source: parsed.source,
    title: parsed.title,
    updated: parsed.updated,
    items: parsed.items.map((item): NewsItem => ({
      id: item.id ?? item.link ?? item.title,
      title: item.title,
      link: item.link,
      publishedAt: item.publishedAt,
      summary: item.summary,
      imageUrl: item.imageUrl,
      categories: item.categories
    }))
  };
}

export async function comparePrices(id: string, countries: string[]) {
  const data = await getJson('/api/compare', { id, countries: countries.join(',') });
  const parsed = compareResponseSchema.parse(data);
  return parsed.rows as PriceComparisonRow[];
}

export async function getArtistInfo(name: string, locale: 'en' | 'zh') {
  const data = await getJson('/api/artist-info', { name, lang: locale });
  return artistInfoSchema.parse(data) as ArtistInfo;
}

export function normalizeSearchItem(raw: Record<string, unknown>): SearchItem | null {
  const rawId = raw.trackId ?? raw.collectionId ?? raw.artistId ?? raw.adamId ?? raw.trackCensoredName ?? raw.collectionCensoredName;
  if (rawId === undefined || rawId === null) return null;

  const title = asString(raw.trackName ?? raw.collectionName ?? raw.artistName ?? raw.trackCensoredName ?? raw.collectionCensoredName) || 'Untitled';
  const subtitle = [
    asString(raw.artistName),
    asString(raw.collectionName),
    asString(raw.primaryGenreName)
  ].filter((part, index, array) => part && array.indexOf(part) === index).slice(0, 2).join(' • ');
  const artworkUrl = upgradeArtwork(asString(raw.artworkUrl100 ?? raw.artworkUrl60 ?? raw.artworkUrl512));

  return {
    id: String(rawId),
    rawId: rawId as string | number,
    wrapperType: asString(raw.wrapperType),
    kind: asString(raw.kind),
    title,
    subtitle,
    artistName: asString(raw.artistName),
    collectionName: asString(raw.collectionName),
    artworkUrl,
    previewUrl: asString(raw.previewUrl),
    officialUrl: asString(raw.trackViewUrl ?? raw.collectionViewUrl ?? raw.artistViewUrl),
    collectionId: asId(raw.collectionId),
    artistId: asId(raw.artistId),
    country: asString(raw.country),
    currency: asString(raw.currency),
    price: asNumber(raw.trackPrice ?? raw.collectionPrice ?? raw.price),
    releaseDate: asString(raw.releaseDate),
    primaryGenreName: asString(raw.primaryGenreName),
    description: asString(raw.longDescription ?? raw.description ?? raw.shortDescription),
    contentAdvisoryRating: asString(raw.contentAdvisoryRating),
    trackCount: asNumber(raw.trackCount),
    raw
  };
}

function normalizeLegacyRssItem(raw: Record<string, unknown>, feed: LegacyRssParams['feed']): SearchItem | null {
  const title = readLegacyLabel(raw['im:name']);
  const rawId = readLegacyAttribute(raw.id, 'im:id') ?? readLegacyLabel(raw.id) ?? title;
  if (!title || !rawId) return null;

  const artistName = readLegacyLabel(raw['im:artist']);
  const genre = readLegacyCategory(raw.category);
  const priceValue = Number(readLegacyAttribute(raw['im:price'], 'amount'));
  const price = Number.isFinite(priceValue) ? priceValue : null;
  const currency = readLegacyAttribute(raw['im:price'], 'currency');
  const releaseDate = readLegacyAttribute(raw['im:releaseDate'], 'label') ?? readLegacyLabel(raw['im:releaseDate']);
  const artworkUrl = upgradeArtwork(readLegacyImage(raw['im:image']));
  const officialUrl = readLegacyLink(raw.link);
  const kind = feed === 'topmovies' ? 'feature-movie' : feed === 'toptvepisodes' ? 'tv-episode' : 'tv-season';

  return {
    id: String(rawId),
    rawId,
    wrapperType: 'track',
    kind,
    title,
    subtitle: [artistName, genre].filter(Boolean).join(' • '),
    artistName,
    artworkUrl,
    officialUrl,
    currency,
    price,
    releaseDate,
    primaryGenreName: genre,
    description: readLegacyLabel(raw.summary),
    raw
  };
}

export function buildQuery(params: Record<string, string | number | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    query.set(key, String(value));
  }
  return query.toString();
}

async function getJson(path: string, params: Record<string, string | number | undefined>) {
  const query = buildQuery(params);
  const response = await fetch(`${path}?${query}`);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with ${response.status}`);
  }
  return response.json() as Promise<unknown>;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asId(value: unknown): number | string | undefined {
  if (typeof value === 'number' || typeof value === 'string') return value;
  return undefined;
}

function toArray<T>(value: T | T[] | undefined | null): T[] {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

function readLegacyLabel(value: unknown): string | undefined {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  const record = asRecord(value);
  return asString(record?.label ?? record?.['#text']);
}

function readLegacyAttribute(value: unknown, attribute: string): string | undefined {
  const attributes = asRecord(asRecord(value)?.attributes);
  return asString(attributes?.[attribute]);
}

function readLegacyCategory(value: unknown): string | undefined {
  const attributes = asRecord(asRecord(value)?.attributes);
  return asString(attributes?.label ?? attributes?.term);
}

function readLegacyImage(value: unknown): string | undefined {
  const images = toArray(value);
  return readLegacyLabel(images[images.length - 1]);
}

function readLegacyLink(value: unknown): string | undefined {
  const links = toArray(value);
  const alternate = links.find((item) => readLegacyAttribute(item, 'rel') === 'alternate') ?? links[0];
  return readLegacyAttribute(alternate, 'href') ?? readLegacyLabel(alternate);
}

export function upgradeArtwork(value?: string) {
  if (!value) return undefined;
  return value.replace(/\d+x\d+bb\.(jpg|png|webp)$/i, '1000x1000bb.$1');
}

export function formatPrice(price: number | null | undefined, currency?: string | null) {
  if (price === null || price === undefined) return currency ? currency : 'Free';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(price);
  } catch {
    return `${currency ?? ''} ${price}`.trim();
  }
}

export function formatDate(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}
